import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import SEO from '../components/SEO';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const BeforeAfter = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentGallery, setCurrentGallery] = useState([]);

  const openLightbox = (images, index) => {
    setCurrentGallery(images.map(src => ({ src })));
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const transformations = [
    {
      id: 1,
      title: 'Living Room Transformation - Fireplace to Wall for More Space',
      location: 'Ipswich, Suffolk',
      before: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/lb326xwl_IMG_0082.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/1dkj74ts_IMG_9468.jpeg',
      description: 'Complete living room transformation - removed old fireplace and created a beautiful plastered wall with spotlights for more living space'
    },
    {
      id: 2,
      title: 'Under Stairs TV Wall & Storage Transformation',
      location: 'Ipswich, Suffolk',
      before: 'https://customer-assets.emergentagent.com/job_buildersite/artifacts/jdasihfd_IMG_9340.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_buildersite/artifacts/g7sq8t10_IMG_9470.jpeg',
      description: 'Custom built TV wall media unit under the stairs for the living room with hidden storage behind - maximising space and style'
    },
    {
      id: 3,
      title: 'Open Plan Living Space Renovation',
      location: 'Ipswich, Suffolk',
      before: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/jp28qgx1_IMG_0086.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/getdwvgf_IMG_9472.jpeg',
      description: 'Complete structural renovation - from construction phase to finished wall with built-in shelving'
    },
    {
      id: 4,
      title: 'External Garden Transformation',
      location: 'Ipswich, Suffolk',
      before: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/wdpazmkd_IMG_4857.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/754re43n_IMG_5039.jpeg',
      description: 'Complete garden transformation - from overgrown space to beautiful decking with brick wall'
    },
    {
      id: 5,
      title: 'Window & Door Replacement',
      location: 'Suffolk',
      before: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/1szronin_IMG_0144.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/z9agkc56_IMG_9481.jpeg',
      description: 'Complete window and door replacement - from old diamond-patterned windows to modern double glazing with new front door'
    },
    {
      id: 6,
      title: 'Door Painting & Restoration',
      location: 'Ipswich, Suffolk',
      before: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/zqrssbyn_IMG_4328.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/ubsa11d8_IMG_4335.jpeg',
      description: 'Professional door restoration and repainting - from faded and damaged to vibrant blue finish'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Before & After - Renovation Transformations Ipswich"
        description="See stunning before and after photos of T&M Construction projects. Kitchen renovations, bathroom makeovers, house extensions in Ipswich, Suffolk. View our quality work."
        keywords="before after renovation, transformation photos Ipswich, renovation results Suffolk, kitchen makeover before after, bathroom renovation photos, house extension gallery"
        canonical="/before-after"
      />
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Before & After</h1>
            <p className="text-xl text-gray-600">
              See the incredible transformations we've achieved for our clients across Suffolk, Essex, and London
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {transformations.map((project) => (
              <div key={project.id} className="max-w-6xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{project.title}</h2>
                  <div className="flex items-center justify-center space-x-2 text-gray-600 mb-2">
                    <LucideIcons.MapPin className="w-5 h-5" />
                    <span>{project.location}</span>
                  </div>
                  <p className="text-gray-600">{project.description}</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div 
                    className="relative group cursor-pointer"
                    onClick={() => openLightbox([project.before, project.after], 0)}
                  >
                    <div className="absolute top-4 left-4 z-10">
                      <span className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                        BEFORE
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-xl flex items-center justify-center">
                      <LucideIcons.ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <img 
                      src={project.before}
                      alt={`${project.title} - Before`}
                      className="w-full h-[400px] object-cover rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div 
                    className="relative group cursor-pointer"
                    onClick={() => openLightbox([project.before, project.after], 1)}
                  >
                    <div className="absolute top-4 left-4 z-10">
                      <span className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                        AFTER
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-xl flex items-center justify-center">
                      <LucideIcons.ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <img 
                      src={project.after}
                      alt={`${project.title} - After`}
                      className="w-full h-[400px] object-cover rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>

                <div className="mt-8 p-6 bg-orange-50 rounded-xl border-2 border-orange-200">
                  <div className="flex items-start space-x-3">
                    <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Transformation Highlights</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• Premium quality materials and finishes</li>
                        <li>• Professional craftsmanship throughout</li>
                        <li>• Completed on time and within budget</li>
                        <li>• Fully satisfied customer</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready for Your Own Transformation?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you create the space you've always dreamed of
          </p>
          <a href="/contact">
            <button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
              Start Your Project Today
            </button>
          </a>
        </div>
      </section>

      {/* Lightbox Component */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={currentGallery}
        index={lightboxIndex}
      />
    </div>
  );
};

export default BeforeAfter;